import { useEffect, useState } from "react";
import { Trophy, Send, Save, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const ContestAdminCard = ({ onUnauthorized }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [announcing, setAnnouncing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/referral-contest", { headers: authHeader() });
        setCfg(res.data);
      } catch (e) {
        if (e?.response?.status === 401 && onUnauthorized) onUnauthorized();
      }
    })();
  }, [onUnauthorized]);

  const save = async (next) => {
    setSaving(true);
    try {
      const body = next || cfg;
      const res = await api.post("/admin/referral-contest", {
        enabled: !!body.enabled, prize_1: body.prize_1 || "$60",
        prize_2: body.prize_2 || null, prize_3: body.prize_3 || null,
        starts_at: body.starts_at || null, ends_at: body.ends_at || null,
        note: body.note || null,
      }, { headers: authHeader() });
      setCfg(res.data);
      toast.success("Contest saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const announce = async () => {
    setAnnouncing(true);
    try {
      await api.post("/admin/referral-contest/announce", {}, { headers: authHeader() });
      toast.success("Contest posted to Telegram 📣");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Announce failed");
    } finally {
      setAnnouncing(false);
    }
  };

  if (!cfg) return null;

  return (
    <div data-testid="contest-admin-card" className="vts-card p-4 mt-6 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Trophy className="h-4 w-4 text-amber-400" />
        <h3 className="font-heading font-semibold text-base text-white">Monthly Referral Contest</h3>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-vts-silver/55 font-mono">
            {cfg.enabled ? "Live" : "Off"}
          </span>
          <Switch data-testid="contest-enabled-toggle" checked={!!cfg.enabled}
            onCheckedChange={(v) => { const next = { ...cfg, enabled: v }; setCfg(next); save(next); }} />
        </div>
      </div>
      <p className="text-[11px] text-vts-silver/60 font-mono">
        Promoters compete on referrals bound this month. Banner shows on every user's Referrals tab. Standings reset automatically on the 1st.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {["prize_1", "prize_2", "prize_3"].map((k, i) => (
          <div key={k}>
            <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">{["🥇 1st", "🥈 2nd", "🥉 3rd"][i]}</label>
            <input data-testid={`contest-${k}-input`} value={cfg[k] || ""}
              onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
              placeholder={i === 0 ? "$50" : "optional"}
              className="w-full mt-0.5 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-1.5 text-xs text-white font-mono outline-none focus:border-amber-400/60" />
          </div>
        ))}
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Fine print (shown on banner)</label>
        <input data-testid="contest-note-input" value={cfg.note || ""}
          onChange={(e) => setCfg({ ...cfg, note: e.target.value })}
          className="w-full mt-0.5 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-1.5 text-xs text-white font-mono outline-none focus:border-amber-400/60" />
      </div>
      <div className="flex gap-2">
        <button data-testid="contest-save-btn" onClick={() => save()} disabled={saving}
          className="flex items-center gap-1.5 rounded-lg bg-vts-blue px-4 py-2 text-xs font-bold text-white hover:bg-vts-blueHover transition disabled:opacity-50">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
        <button data-testid="contest-announce-btn" onClick={announce} disabled={announcing || !cfg.enabled}
          className="flex items-center gap-1.5 rounded-lg border border-sky-500/50 bg-sky-500/15 px-4 py-2 text-xs font-bold text-sky-300 hover:bg-sky-500/25 transition disabled:opacity-40">
          {announcing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Announce on Telegram
        </button>
      </div>
    </div>
  );
};

export default ContestAdminCard;
